import { useState } from 'react';
import { api, ApiError } from '../api/client';

type Beat = { logo: string; label: string; accent: string; graphic: '' | 'dashboard' | 'nodes' | 'chat' };
type Bg = 'layers' | 'ai' | 'city';
type LayersPreset = 'particles' | 'tunnel' | 'waves' | 'grid';

const MUSICAS = ['epic-tech', 'calm-corporate', 'energetic-reel'];

// Presets de capas → array de layers, parametrizado por acento.
function layersFromPreset(preset: LayersPreset, accent: string) {
  const base = { type: 'gradient', from: '#0b1430', to: '#03050c' };
  const vig = { type: 'vignette', strength: 0.9 };
  const glow = { type: 'glow', color: accent, size: 42 };
  switch (preset) {
    case 'tunnel':
      return [{ type: 'gradient', kind: 'radial', from: '#10122a', to: '#03050c' }, { type: 'rings', colors: [accent, '#d946ef'], count: 16 }, glow, vig];
    case 'waves':
      return [base, { type: 'gridFloor', color: accent, speed: 8 }, { type: 'waves', colors: [accent, '#d946ef'], lines: 6, amplitude: 70 }, glow, vig];
    case 'grid':
      return [base, { type: 'gridFloor', color: accent, speed: 9 }, { type: 'particles', colors: [accent, '#d946ef'], count: 70 }, vig];
    case 'particles':
    default:
      return [base, { type: 'particles', colors: [accent, '#d946ef'], count: 90 }, glow, vig];
  }
}

export function VideoPage() {
  const [mode, setMode] = useState<'form' | 'json'>('form');

  // --- form state ---
  const [aspect, setAspect] = useState('16:9');
  const [dur, setDur] = useState(12);
  const [bg, setBg] = useState<Bg>('layers');
  const [preset, setPreset] = useState<LayersPreset>('particles');
  const [fondoPrompt, setFondoPrompt] = useState('ciudad neón fly-through, cinematográfico');
  const [musica, setMusica] = useState('epic-tech');
  const [wordmark, setWordmark] = useState('XLearning Space');
  const [tagline, setTagline] = useState('Aprendizaje inmersivo');
  const [acento, setAcento] = useState('#22d3ee');
  const [beats, setBeats] = useState<Beat[]>([
    { logo: 'xvision', label: 'Genera con IA', accent: '#22d3ee', graphic: 'dashboard' },
    { logo: 'xagent', label: 'Vende con IA', accent: '#a3e635', graphic: 'chat' },
  ]);

  const [jsonText, setJsonText] = useState('');
  const [status, setStatus] = useState<'idle' | 'queued' | 'rendering' | 'done' | 'error'>('idle');
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const buildSpec = () => {
    const spec: Record<string, unknown> = {
      aspect,
      duracionSeg: dur,
      bg,
      musica,
      marca: { wordmark, tagline, acento },
      beats: beats.map((b) => {
        const o: Record<string, unknown> = { logo: b.logo, label: b.label, accent: b.accent };
        if (b.graphic) o.graphic = b.graphic;
        return o;
      }),
    };
    if (bg === 'layers') spec.layers = layersFromPreset(preset, acento);
    if (bg === 'ai') spec.fondoPrompt = fondoPrompt;
    return spec;
  };

  const apiErr = (e: unknown) =>
    e instanceof ApiError
      ? typeof e.body === 'object' && e.body && 'message' in e.body
        ? String((e.body as { message: unknown }).message)
        : `${e.status} ${e.message}`
      : String(e);

  const render = async () => {
    setError(null);
    let spec: unknown;
    if (mode === 'json') {
      try {
        spec = JSON.parse(jsonText);
      } catch {
        setError('El spec no es JSON válido.');
        setStatus('error');
        return;
      }
    } else {
      spec = buildSpec();
    }
    setStatus('queued');
    setUrl(null);
    try {
      const { jobId } = await api.post<{ ok: boolean; jobId: string }>('/render/spec', {
        spec,
        name: `spot-${Date.now()}`,
      });
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const job = await api.get<{ status: string; url?: string; error?: string }>(`/render/jobs/${jobId}`);
        if (job.status === 'rendering') setStatus('rendering');
        if (job.status === 'done' && job.url) {
          setUrl(job.url);
          setStatus('done');
          return;
        }
        if (job.status === 'error') {
          setError(job.error ?? 'El render falló.');
          setStatus('error');
          return;
        }
      }
      setError('Timeout esperando el render.');
      setStatus('error');
    } catch (e) {
      setError(apiErr(e));
      setStatus('error');
    }
  };

  const updateBeat = (i: number, patch: Partial<Beat>) =>
    setBeats((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  const inp = 'bg-slate-900 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200 focus:outline-none focus:border-vision';

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold text-slate-100">Video de presentación</h1>
        <div className="flex gap-1 text-xs">
          <button onClick={() => setMode('form')} className={`px-2 py-1 rounded ${mode === 'form' ? 'bg-vision-dark text-white' : 'bg-slate-800 text-slate-400'}`}>Formulario</button>
          <button onClick={() => { setJsonText(JSON.stringify(buildSpec(), null, 2)); setMode('json'); }} className={`px-2 py-1 rounded ${mode === 'json' ? 'bg-vision-dark text-white' : 'bg-slate-800 text-slate-400'}`}>JSON</button>
        </div>
      </div>
      <p className="text-slate-400 text-sm mb-4">Armá un spot. La versión conversacional con el experto vive en un chat tipo "Video de presentación (spot)".</p>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-3">
          {mode === 'json' ? (
            <textarea value={jsonText} onChange={(e) => setJsonText(e.target.value)} spellCheck={false} className={`min-h-[440px] font-mono text-xs ${inp}`} />
          ) : (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <label className="flex flex-col gap-1 text-xs text-slate-400">Aspecto
                  <select value={aspect} onChange={(e) => setAspect(e.target.value)} className={inp}><option>16:9</option><option>9:16</option><option>1:1</option></select>
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-400">Duración (s)
                  <input type="number" min={4} max={30} value={dur} onChange={(e) => setDur(Number(e.target.value))} className={inp} />
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-400">Acento
                  <input type="color" value={acento} onChange={(e) => setAcento(e.target.value)} className="h-8 w-full bg-slate-900 border border-slate-800 rounded" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-xs text-slate-400">Fondo
                  <select value={bg} onChange={(e) => setBg(e.target.value as Bg)} className={inp}><option value="layers">Capas (código, $0)</option><option value="ai">IA (por prompt)</option><option value="city">Ciudad wireframe</option></select>
                </label>
                {bg === 'layers' && (
                  <label className="flex flex-col gap-1 text-xs text-slate-400">Estilo
                    <select value={preset} onChange={(e) => setPreset(e.target.value as LayersPreset)} className={inp}><option value="particles">Partículas</option><option value="tunnel">Túnel</option><option value="waves">Ondas</option><option value="grid">Grilla</option></select>
                  </label>
                )}
                {bg === 'ai' && (
                  <label className="flex flex-col gap-1 text-xs text-slate-400 col-span-1">Prompt del fondo
                    <input value={fondoPrompt} onChange={(e) => setFondoPrompt(e.target.value)} className={inp} />
                  </label>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-xs text-slate-400">Música
                  <select value={musica} onChange={(e) => setMusica(e.target.value)} className={inp}>{MUSICAS.map((m) => <option key={m}>{m}</option>)}</select>
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-400">Wordmark
                  <input value={wordmark} onChange={(e) => setWordmark(e.target.value)} className={inp} />
                </label>
              </div>
              <label className="flex flex-col gap-1 text-xs text-slate-400">Tagline (cierre)
                <input value={tagline} onChange={(e) => setTagline(e.target.value)} className={inp} />
              </label>

              <div className="text-xs text-slate-400 pt-1">Beats</div>
              {beats.map((b, i) => (
                <div key={i} className="grid grid-cols-12 gap-1 items-center">
                  <input value={b.logo} onChange={(e) => updateBeat(i, { logo: e.target.value })} placeholder="logo / #ref" className={`col-span-3 ${inp}`} />
                  <input value={b.label} onChange={(e) => updateBeat(i, { label: e.target.value })} placeholder="rótulo" className={`col-span-4 ${inp}`} />
                  <input type="color" value={b.accent} onChange={(e) => updateBeat(i, { accent: e.target.value })} className="col-span-1 h-8 bg-slate-900 border border-slate-800 rounded" />
                  <select value={b.graphic} onChange={(e) => updateBeat(i, { graphic: e.target.value as Beat['graphic'] })} className={`col-span-3 ${inp}`}><option value="">(captura)</option><option value="dashboard">dashboard</option><option value="nodes">nodos</option><option value="chat">chat</option></select>
                  <button onClick={() => setBeats((bs) => bs.filter((_, j) => j !== i))} className="col-span-1 text-slate-500 hover:text-red-400">✕</button>
                </div>
              ))}
              <button onClick={() => setBeats((bs) => [...bs, { logo: 'xvision', label: 'Nuevo beat', accent: acento, graphic: 'dashboard' }])} className="text-xs text-vision hover:underline">+ agregar beat</button>
            </div>
          )}
          <button onClick={render} disabled={status === 'rendering' || status === 'queued'} className="px-4 py-2 rounded-lg bg-vision-dark hover:bg-vision text-white text-sm font-medium disabled:opacity-50 self-start">
            {status === 'queued' ? 'En cola…' : status === 'rendering' ? 'Renderizando…' : 'Renderizar'}
          </button>
        </div>

        <div className="flex flex-col">
          <label className="text-xs text-slate-400 mb-1">Resultado</label>
          <div className="flex-1 min-h-[440px] bg-slate-900 border border-slate-800 rounded-lg flex items-center justify-center p-3">
            {(status === 'queued' || status === 'rendering') && (
              <div className="text-slate-400 text-sm text-center">{status === 'queued' ? 'En cola…' : 'Renderizando el video…'}<div className="text-xs text-slate-500 mt-1">(~30–60s)</div></div>
            )}
            {status === 'error' && <div className="text-red-400 text-sm text-center px-4 whitespace-pre-wrap">{error}</div>}
            {status === 'done' && url && (
              <div className="w-full">
                <video src={url} controls className="w-full rounded-lg" />
                <a href={url} target="_blank" rel="noreferrer" className="block text-center text-xs text-vision mt-2 hover:underline">Abrir / descargar</a>
              </div>
            )}
            {status === 'idle' && <div className="text-slate-600 text-sm">El video aparece acá.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
